import pb from '../lib/pocketbase';
import { brevoEmailService } from './brevoEmailService';

/**
 * Service for managing partner admin activation
 */
class ActivationService {
  /**
   * Generate a secure random activation token
   */
  private generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate activation token info (public method)
   */
  generateActivationToken(): { token: string; expiresAt: Date } {
    const token = this.generateToken();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 48);
    return { token, expiresAt };
  }

  /**
   * Store activation token in database
   */
  async storeActivationToken(
    adminId: string,
    token: string,
    expiresAt: Date
  ): Promise<void> {
    await pb.collection('users').update(adminId, {
      activation_token: token,
      activation_token_expires: expiresAt.toISOString(),
      is_activated: false
    });
  }

  /**
   * Send activation email
   * NOTE: In production, the email is sent by the PocketBase hook (user_activation.pb.js)
   * This method is kept for development/fallback purposes only
   */
  async sendActivationEmail(
    email: string,
    name: string,
    token: string
  ): Promise<void> {
    // In production, the PocketBase hook handles email sending server-side
    // This is more secure as the API key stays on the server
    console.log('📧 Activation email will be sent by PocketBase hook');
    console.log('📧 User:', email, '| Token length:', token.length);
    
    // Only attempt client-side email in development if Brevo key is available
    if (import.meta.env.DEV && import.meta.env.VITE_BREVO_API_KEY) {
      const activationUrl = `${window.location.origin}/activate-account?token=${token}`;
      console.log('🔗 DEV MODE - Activation URL:', activationUrl);
      
      // Optionally send via client in dev mode for testing
      const emailResult = await brevoEmailService.sendEmail({
        to: [{ email, name }],
        subject: 'Activate Your Partner Admin Account',
        htmlContent: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #2563eb;">Welcome to Click and Claim!</h2>
            <p>Hello ${name},</p>
            <p>Your partner admin account has been created. To activate your account and set your password, please click the button below:</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${activationUrl}" 
                 style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">
                Activate Account
              </a>
            </div>
            <p style="color: #666; font-size: 14px;">
              This activation link will expire in 48 hours. If you didn't request this account, please ignore this email.
            </p>
            <p style="color: #666; font-size: 14px;">
              If the button doesn't work, copy and paste this link into your browser:<br>
              <a href="${activationUrl}" style="color: #2563eb; word-break: break-all;">${activationUrl}</a>
            </p>
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">
            <p style="color: #999; font-size: 12px;">
              This is an automated message. Please do not reply to this email.
            </p>
          </div>
        `,
        textContent: `Welcome to Click and Claim!

Hello ${name},

Your partner admin account has been created. To activate your account and set your password, please visit this link:

${activationUrl}

This activation link will expire in 48 hours. If you didn't request this account, please ignore this email.

---
This is an automated message. Please do not reply to this email.`
      });
      
      if (!emailResult.success) {
        console.warn('⚠️ DEV: Client-side email failed, but PocketBase hook should handle it');
      }
    }
  }

  /**
   * Create activation token and send email (legacy method - kept for compatibility)
   */
  async createActivationToken(
    adminId: string,
    email: string,
    name: string
  ): Promise<{ token: string; expiresAt: Date }> {
    const token = this.generateToken();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 48); // Token valid for 48 hours

    try {
      // Store token in database (users collection for PocketBase auth)
      await pb.collection('users').update(adminId, {
        activation_token: token,
        activation_token_expires: expiresAt.toISOString(),
        is_activated: false
      });

      // Send activation email
      const activationUrl = `${window.location.origin}/activate-account?token=${token}`;
      
      console.log('📧 Preparing to send activation email:', {
        to: email,
        name: name,
        activationUrl: activationUrl,
        tokenLength: token.length
      });
      
      const emailResult = await brevoEmailService.sendEmail({
        to: [{ email, name }],
        subject: 'Activate Your Partner Admin Account',
        htmlContent: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #2563eb;">Welcome to Magic Links Platform!</h2>
            <p>Hello ${name},</p>
            <p>Your partner admin account has been created. To activate your account and set your password, please click the button below:</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${activationUrl}" 
                 style="background-color: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">
                Activate Account
              </a>
            </div>
            <p style="color: #666; font-size: 14px;">
              This activation link will expire in 48 hours. If you didn't request this account, please ignore this email.
            </p>
            <p style="color: #666; font-size: 14px;">
              If the button doesn't work, copy and paste this link into your browser:<br>
              <a href="${activationUrl}" style="color: #2563eb; word-break: break-all;">${activationUrl}</a>
            </p>
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">
            <p style="color: #999; font-size: 12px;">
              This is an automated message. Please do not reply to this email.
            </p>
          </div>
        `,
        textContent: `Welcome to Magic Links Platform!

Hello ${name},

Your partner admin account has been created. To activate your account and set your password, please visit this link:

${activationUrl}

This activation link will expire in 48 hours. If you didn't request this account, please ignore this email.

---
This is an automated message. Please do not reply to this email.`
      });
      
      // Check if email was sent successfully
      if (!emailResult.success) {
        // In development, log the activation link to console as a fallback
        if (import.meta.env.DEV) {
          console.error('❌ Email failed to send, but activation link is available:');
          console.log('🔗 ACTIVATION LINK (copy this):', activationUrl);
          console.log('📋 Send this link to:', email);
          console.log('⏰ Link expires:', expiresAt.toLocaleString());
          console.warn('⚠️ For production, fix the email service configuration');
          
          // Still return success in dev mode so admin can use the console link
          return { token, expiresAt };
        }
        
        throw new Error(emailResult.error || 'Failed to send activation email');
      }

      return { token, expiresAt };
    } catch (error: any) {
      console.error('Failed to create activation token:', error);
      
      // Provide more specific error messages
      if (error?.status === 404) {
        throw new Error('Database error: User record not found. Please try again.');
      } else if (error?.message?.includes('api-key')) {
        throw new Error('Email service not configured. Please contact administrator.');
      } else if (error?.response?.message) {
        throw new Error(`Failed to send activation email: ${error.response.message}`);
      } else {
        throw new Error('Failed to send activation email. Please try again.');
      }
    }
  }

  /**
   * Validate activation token
   */
  async validateToken(token: string): Promise<{
    valid: boolean;
    adminId?: string;
    email?: string;
    name?: string;
    error?: string;
  }> {
    try {
      const records = await pb.collection('users').getFullList({
        filter: `activation_token = "${token}"`
      });

      if (records.length === 0) {
        return { valid: false, error: 'Invalid activation token' };
      }

      const admin = records[0];

      // Check if already activated
      if (admin.is_activated) {
        return { valid: false, error: 'This account has already been activated' };
      }

      // Check if token expired
      const expiresAt = new Date(admin.activation_token_expires);
      if (expiresAt < new Date()) {
        return { valid: false, error: 'Activation token has expired' };
      }

      return {
        valid: true,
        adminId: admin.id,
        email: admin.email,
        name: admin.name
      };
    } catch (error) {
      console.error('Token validation error:', error);
      return { valid: false, error: 'Failed to validate token' };
    }
  }

  /**
   * Activate account with new password
   */
  async activateAccount(token: string, password: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // Validate token first
      const validation = await this.validateToken(token);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Authenticate with the token (which is the temporary password)
      // This allows us to update the password with oldPassword = token
      await pb.collection('users').authWithPassword(validation.email!, token);

      // Now update the password (we have oldPassword = token)
      await pb.collection('users').update(validation.adminId!, {
        password,
        passwordConfirm: password,
        oldPassword: token, // The token was used as the temp password
        is_activated: true,
        status: 'active',
        activation_token: '',
        activation_token_expires: '',
        first_login_completed: true
      });

      // Clear the auth (logout)
      pb.authStore.clear();

      return { success: true };
    } catch (error: any) {
      console.error('Account activation error:', error);
      return {
        success: false,
        error: error.message || 'Failed to activate account'
      };
    }
  }

  /**
   * Resend activation email
   */
  async resendActivationEmail(email: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const records = await pb.collection('users').getFullList({
        filter: `email = "${email}" && is_activated = false`
      });

      if (records.length === 0) {
        return {
          success: false,
          error: 'No pending activation found for this email'
        };
      }

      const admin = records[0];

      // Generate new token
      await this.createActivationToken(admin.id, admin.email, admin.name);

      return { success: true };
    } catch (error: any) {
      console.error('Resend activation error:', error);
      return {
        success: false,
        error: error.message || 'Failed to resend activation email'
      };
    }
  }
}

export const activationService = new ActivationService();
